// recovery-batch-proposal-rules.ts
//
// Orchestrator recovery (3.40.0, D3.2/D3.4): the two refusals only a batch
// from a RECOVERY decision ticket can get — `dangling_dependency` first, then
// `red_anchor_lost`. Called by supervisor-proposal-application.ts after the
// remap, the synthetic supersedes and the DAG check, before any file is
// written (N10). Both codes are on the counted allow-list (F-72): the worker
// retries with the reason in its next prompt.
import type { AutopilotResult } from "./autopilot-result-schema.js";
import type { ControllerLedgerState } from "./controller-redirect-decision-ticket.js";
import {
  danglingDependencyRejection,
  type RecoveryBatchRejection,
} from "./proposal-dangling-dependency-check.js";
import { redAnchorRejection } from "./proposal-red-anchor-check.js";
import type { Ticket } from "./schemas/ticket-schema.js";

/** What the application knows after applying the batch in memory. */
export interface RecoveryBatchPass {
  before: readonly Ticket[];
  after: readonly Ticket[];
  statusAfter: Readonly<Record<string, string>>;
  createdIds: readonly string[];
  /** Plans the synthetic R3 supersede retires in this pass. */
  syntheticSupersededIds: readonly string[];
}

export function recoveryBatchRejection(
  input: {
    store: { state: ControllerLedgerState };
    sourceTicketId?: string;
    proposedChanges: AutopilotResult["proposed_changes"];
  },
  pass: RecoveryBatchPass,
): RecoveryBatchRejection | undefined {
  const check = {
    // The ticket BEFORE this batch: amending itself cannot take it out of scope.
    sourceTicket: pass.before.find((t) => t.id === input.sourceTicketId),
    state: input.store.state,
    before: pass.before,
    after: pass.after,
    statusAfter: pass.statusAfter,
    createdIds: pass.createdIds,
    supersededIds: [
      ...input.proposedChanges.filter((c) => c.op === "supersede").map((c) => c.target),
      ...pass.syntheticSupersededIds,
    ],
  };
  return danglingDependencyRejection(check) ?? redAnchorRejection(check);
}
